import { useEffect, useRef } from 'react';
import { checkBrowser } from './utils';

export const useDraggable = (blockRef) => {
    const isDragging = useRef();
    const startX = useRef();
    const startScrollLeft = useRef();

    useEffect(() => {
        if (!blockRef) return;
        var scrollableElement = blockRef?.current;
        const mousedown = function (e) {
            if (blockRef?.current?.scrollWidth > blockRef?.current?.clientWidth) {
                isDragging.current = true;
                startX.current = e.clientX;
                startScrollLeft.current = scrollableElement.scrollLeft;
                scrollableElement.style.cursor = 'grabbing';
            }
        };
        const mousemove = function (e) {
            if (!isDragging.current) return;

            var deltaX = e.clientX - startX.current;
            scrollableElement.scrollLeft = startScrollLeft.current - deltaX;
        };
        const mouseup = function () {
            isDragging.current = false;
            scrollableElement.style.cursor = '';
        };
        blockRef.current.addEventListener('mousedown', mousedown);
        document.addEventListener('mousemove', mousemove);
        document.addEventListener('mouseup', mouseup);
        return () => {
            blockRef?.current?.removeEventListener('mousedown', mousedown);
            document.removeEventListener('mousemove', mousemove);
            document.removeEventListener('mouseup', mouseup);
        };
    }, [blockRef]);
};

export const useCaptureFrame = () => {};

// хук для зума зображень (приймає в себе контейнер картинки)
export const useImageZoom = (imgContainerRef = {}) => {
    useEffect(() => {
        if (imgContainerRef.current) {
            const container = imgContainerRef.current;
            const image = container.querySelector('img');
            if (!image) return;
            // Створюємо елемент лупи
            const lens = document.createElement('div');
            lens.classList.add('zoom-lens');
            container.appendChild(lens);

            container.addEventListener('mousemove', (e) => {
                const { left, top, width, height } = container.getBoundingClientRect();

                // Отримуємо позицію курсора всередині контейнера
                const x = e.clientX - left;
                const y = e.clientY - top;

                // Встановлюємо позицію лупи на зображенні
                lens.style.left = `${x - lens.offsetWidth / 2}px`;
                lens.style.top = `${y - lens.offsetHeight / 2}px`;

                // Рухаємо зображення всередині лупи
                const zoomLevel = 4; // Рівень збільшення
                image.style.transformOrigin = `${(x / width) * 100}% ${(y / height) * 100}%`;
                image.style.transform = `scale(${zoomLevel})`;
            });

            // Повертаємо зображення до початкового стану при виході курсора з контейнера
            container.addEventListener('mouseleave', () => {
                image.style.transform = 'scale(1)';
            });
        }
    }, [imgContainerRef.current]);
};

export const useFixedOnPageCustom = (ref) => {
    useEffect(() => {
        if (!ref?.current) return;
        let diff = 0;
        const listener = () => {
            if (!ref?.current) return;
            const scrollY = checkBrowser(window.scrollY);
            const topPX = ref.current.getBoundingClientRect().top;
            if (checkBrowser(topPX) <= 0) {
                if (!diff) {
                    diff = scrollY;
                }
                ref.current.style.transform = `translateY(${scrollY - diff}px)`;
            } else {
                ref.current.style.transform = `translateY(0px)`;
            }
        };
        document.addEventListener('scroll', listener);
        return () => {
            document.removeEventListener('scroll', listener);
        };
    }, [ref?.current]);
};

export const useFixedOnPage = (ref, container, offset = 0) => {
    useEffect(() => {
        if (!ref?.current) return;
        let diff;
        const listener = () => {
            const element = ref?.current;
            const elementProps = element.getBoundingClientRect();
            const containerElement = container?.current;
            const containerProps = containerElement?.getBoundingClientRect();
            let scrollY = window.scrollY;
            const topPX = elementProps.top;
            let blockEnded;
            if (containerElement) {
                if (checkBrowser(containerProps.height) - checkBrowser(elementProps.height) - scrollY + diff < 0) {
                    blockEnded = true;
                }
            }
            if (blockEnded) {
                element.style.transform = `translateY(${
                    checkBrowser(containerProps.height) - checkBrowser(elementProps.height) - offset
                }px)`;
            } else if (topPX - offset <= 0) {
                if (!diff) {
                    diff = scrollY;
                }
                element.style.transform = `translateY(${checkBrowser(scrollY - diff)}px)`;
            } else {
                element.style.transform = `translateY(0px)`;
            }
        };
        document.addEventListener('scroll', listener);
        return () => {
            document.removeEventListener('scroll', listener);
        };
    }, [ref?.current, container?.current]);
};

export const useDraggableHeight = (ref) => {
    const isDragging = useRef(false); // Флаг состояния "перетаскивания"
    const startY = useRef(0); // Начальная координата Y
    const scrollTop = useRef(0); // Начальное смещение прокрутки

    useEffect(() => {
        const element = ref.current; // Получаем DOM-элемент из переданного рефа
        if (!element) return;

        const onMouseDown = (e) => {
            isDragging.current = true;
            startY.current = e.pageY - element.offsetTop;
            scrollTop.current = element.scrollTop;
            element.style.cursor = 'grabbing';
        };

        const onMouseMove = (e) => {
            if (!isDragging.current) return;
            const y = e.pageY - element.offsetTop;
            const walk = (y - startY.current) * 2; // Скорость прокрутки
            element.scrollTop = scrollTop.current - walk;
        };

        const onMouseUp = () => {
            isDragging.current = false;
            element.style.cursor = 'grab';
        };

        const onTouchStart = (e) => {
            isDragging.current = true;
            startY.current = e.touches[0].pageY - element.offsetTop;
            scrollTop.current = element.scrollTop;
        };

        const onTouchMove = (e) => {
            if (!isDragging.current) return;
            const y = e.touches[0].pageY - element.offsetTop;
            const walk = (y - startY.current) * 2;
            element.scrollTop = scrollTop.current - walk;
        };

        const onTouchEnd = () => {
            isDragging.current = false;
        };

        // Добавляем обработчики событий
        element.addEventListener('mousedown', onMouseDown);
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        element.addEventListener('touchstart', onTouchStart);
        document.addEventListener('touchmove', onTouchMove);
        document.addEventListener('touchend', onTouchEnd);

        // Удаляем обработчики событий при размонтировании
        return () => {
            element.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            element.removeEventListener('touchstart', onTouchStart);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
        };
    }, [ref]); // Зависимость - реф на DOM-элемент
};
